import { MouseEvent, useEffect, useState } from "react";
import { MapItem, MenusItem, path, UserInfo as UserInfoType } from "./Common";
import styles from "./MapListView.module.css";
import Pagination from "./Pagination";
import ContentTitle from "./ContentTitle";
import MapListItem from "./MapListItem";
import { useNavigate } from "react-router";
import NoContent from "./NoContent";
import { SearchFilters, SearchOptions } from "./Search";
import DataProvider from "../../data_provider/DataProvider";
import { ListSearchReqBody } from "../../data_provider/gateways/MapGateway";
import { ResponseType } from "../../data_provider/gateways/Gateway";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import LoadingSpinner from "../common/LoadingSpinner";
import LoginForm from "../login/LoginForm";
import SignUpModal from "../login/SignUpModal";
import { useCookies } from "react-cookie";
import { useLoginStatus } from "../../hooks/useLoginStatus";

/**
 * @author bakim
 * @date 2024-08-20 10:55:37
 * @remark 지도 목록 아이템
 * @param updateData (required): 데이터 변경 함수
 * @param {string} title: 리스트 제목
 * @param {boolean}isMyMap: 내 지도 목록 여부
 * @returns MapItem
 */

interface MapListViewProps {
    updateData: (body: ListSearchReqBody) => Promise<ResponseType>;
    title?: string;
    isMyMap?: boolean;
    userInfo?: UserInfoType;
}

export interface MapSearchOptions {
    title?: string;
    description?: string;
    keyword?: string;
    username?: string;
}

type MapFilterKey = Exclude<keyof MapSearchOptions, undefined>;

const MapListView = ({ ...props }: MapListViewProps) => {
    const { ModalComponent, openModal, closeModal } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const [cookies] = useCookies(["KMUI"]);

    const [data, setData] = useState<MapItem[]>([]);
    const [currentPageNo, setCurrentPageNo] = useState<number>(1);
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);
    const [searchOptions, setSearchOptions] = useState<SearchOptions>();
    const [lastPageNo, setLastPageNo] = useState<number>(0);

    const navigate = useNavigate();
    const isLogin = useLoginStatus(); // login검증 커스텀 훅 이용 | smyun | 250721

    // TODO: 현재 화면이 열려있는 채로 로그인 정보가 바뀌는 경우 props.userInfo는 최신 값을 가지고 있지 않아 새로고침이 필요함
    // 사용자 정보가 AccountManager instance에 저장되므로, 해당 함수를 사용하여 사용자 등급이나 권한을 확인하는 코드로 수정 필요

    const userId = cookies.KMUI ?? undefined;

    // 마운트 시 무조건 한번 실행됨
    useEffect(() => {
        getMapList(1);
    }, [searchOptions]);

    // 더보기 메뉴를 열어둔 상태로 상단 로그인 하는 경우에 화면에 더보기 메뉴가 남아있지 않도록 닫아주는 로직 추가 | smyun | 250709
    useEffect(() => {
        // 더보기 메뉴 감지
        const handleClickOutside = (e: globalThis.MouseEvent) => {
            const target = e.target as HTMLElement;

            // 더보기 메뉴가 열려있고, 메뉴 외부 클릭 시 닫기
            if (selectedIdx !== -1 && !target.closest(`.mapListItemMenu`)) {
                setSelectedIdx(-1);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [selectedIdx]);

    const getSearchFilters = () => {
        const searchFilters: SearchFilters[] = [
            {
                value: "default",
                name: "전체",
            },
            {
                value: "title",
                name: "지도명",
            },
            {
                value: "description",
                name: "설명",
            },
            {
                value: "keyword",
                name: "키워드",
            },
        ];

        !props.isMyMap &&
            searchFilters.push({
                value: "username",
                name: "별명",
            });

        return searchFilters;
    };

    /**
     * 지도 조회
     * @param no: 페이지 넘버
     * @returns MapItem[]
     */
    const getMapList = async (pageNo: number) => {
        const body = {
            page: pageNo,
        } as ListSearchReqBody;

        if (searchOptions) {
            body.filter = {} as MapSearchOptions;
            const key = searchOptions.filter as MapFilterKey;
            if (!searchOptions.filter || searchOptions.filter === "default") {
                body.filter = {
                    title: searchOptions.keyword,
                    description: searchOptions.keyword,
                    keyword: searchOptions.keyword,
                };
                if (!props.isMyMap) {
                    body.filter.username = searchOptions.keyword;
                }
            } else {
                body.filter[key] = searchOptions.keyword;
            }
        }

        const res = await props.updateData(body);

        if (res.flag) {
            setLastPageNo(res.result ? res.result.pages : 0);
            setData(res.result ? res.result.list : []);
        } else {
            props.isMyMap && res.status && res.status === 403 && navigate("/");
            //TODO 에러 상황 별 화면 처리
        }
    };

    const onClickItem = (mapId: string) => {
        const url = path.MAP;
        const params = `id=${mapId}`;
        const request = `${url}?${params}`;

        // navigate(request);
        window.open(request, "_blank"); // 새창(또는 새탭)으로 열기
    };

    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={() => closeModal()} />,
            modalButtons: null,
            size: "normal",
        });
    };
    const loginGuide = () => {
        openModal({
            title: "안내",
            children: (
                <>
                    로그인이 필요한 서비스입니다.
                    <br />
                    로그인을 하시겠습니까?
                </>
            ),
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () =>
                                openModal({
                                    title: "로그인",
                                    children: <LoginForm onClose={() => closeModal()} onClickSignUp={onClickSignUp} goRefresh={true} />,
                                    modalButtons: null,
                                    size: "wide",
                                    disableOverlayClose: true,
                                }),
                        },
                    ]}
                />
            ),
        });
    };

    // 목록 항목 별 메뉴 콜백 함수
    const onClickDeleteMap = async (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setSelectedIdx(-1);

        if (isLogin) {
            openModal({
                title: "안내",
                children: "삭제를 진행하시겠습니까?",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "취소",
                                color: "Secondary",
                                onClick: () => {
                                    closeModal();
                                    return;
                                },
                            },
                            {
                                label: "삭제",
                                color: "Danger",
                                onClick: async () => {
                                    const element = data![selectedIdx];
                                    const result = await DataProvider.DeleteMap({ mapid: element.mapId });

                                    if (result.flag) {
                                        getMapList(currentPageNo);
                                        closeModal();
                                    } else {
                                        result.status &&
                                            result.status === 403 &&
                                            openModal({
                                                title: "경고",
                                                children: "삭제 권한이 없습니다.",
                                                modalButtons: (
                                                    <ModalButtons
                                                        buttonCount={1}
                                                        buttons={[
                                                            {
                                                                label: "확인",
                                                                color: "Danger",
                                                                onClick: () => {
                                                                    closeModal();
                                                                },
                                                            },
                                                        ]}
                                                    />
                                                ),
                                            });
                                        // TODO 에러 상황 별 화면 처리
                                    }
                                    // setSelectedIdx(-1);
                                },
                            },
                        ]}
                    />
                ),
            });
        } else {
            loginGuide();
        }
    };

    const onClickKMLDownload = async (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setSelectedIdx(-1);

        // KML은 프로사용자에게만 제공 | 250826 | smyun |
        // 사용자 등급 확인
        const response = await DataProvider.GetUserInfo();
        const grade = response.flag ? response.result.membership.gradeCode : 1;

        if (isLogin) {
            if (grade == 1) {
                // 일반회원일 때
                openModal({
                    title: "안내",
                    children: <p>KML 다운 기능은 프로사용자에게만 제공됩니다.</p>,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Secondary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                                {
                                    label: "멤버십 가입하러 가기",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                        navigate(path.PRICE_POLICY);
                                    },
                                },
                            ]}
                        />
                    ),
                });
            } else {
                // 프로 사용자 & 관리자
                const confirmDownload = await new Promise<boolean>((resolve) => {
                    openModal({
                        title: "안내",
                        children: <p>지도 KML을 다운받으시겠습니까?</p>,
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "취소",
                                        color: "Secondary",
                                        onClick: () => {
                                            closeModal();
                                            resolve(false);
                                        },
                                    },
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () => {
                                            closeModal();
                                            resolve(true);
                                        },
                                    },
                                ]}
                            />
                        ),
                    });
                });

                if (!confirmDownload) return;

                try {
                    openModal({
                        title: "KML 파일을 준비중입니다.",
                        children: <LoadingSpinner />,
                    });
                    setIsLoading(true);

                    const element = data![selectedIdx];
                    const res = await DataProvider.GetKML({ mapid: element.mapId, title: element.title });

                    if (res.flag) {
                        const blob = res.result;
                        const link = document.createElement("a");
                        link.href = window.URL.createObjectURL(blob);
                        link.download = data![selectedIdx].title + ".kml";
                        link.click();
                    } else {
                        if (res.status === 403) {
                            loginGuide();
                        }
                    }
                } catch (error) {
                    openModal({
                        title: "안내",
                        children: <p>{String(error)}</p>,
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () => {
                                            closeModal();
                                        },
                                    },
                                ]}
                            />
                        ),
                    });
                } finally {
                    setIsLoading(false);
                    closeModal();
                    // setSelectedIdx(-1);
                }
            }
        } else {
            loginGuide();
        }
    };

    // 더보기 메뉴
    const getMenuItem = (logined: boolean, deleteable: boolean): MenusItem[] => {
        const result: MenusItem[] = [];

        result.push({ title: "KML다운로드", onClickItem: onClickKMLDownload });

        if (logined && deleteable) {
            result.push({ title: "삭제", onClickItem: onClickDeleteMap });
        }
        return result;
    };

    const onPageChanged = (pageNo: number) => {
        getMapList(pageNo);
        setCurrentPageNo(pageNo);
    };

    const doSearch = (searchOptions: SearchOptions) => {
        setSearchOptions(searchOptions.keyword ? searchOptions : undefined);
        setCurrentPageNo(1);
    };

    const renderItem = (item: MapItem, index: number) => {
        const isActive = index === selectedIdx;

        const onClickMenus = (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            isActive ? setSelectedIdx(-1) : setSelectedIdx(index);
        };

        // const deleteAble = props.isMyMap || props.userInfo?.membership?.gradeCode === 0 || item.userId === props.userInfo?.userId;
        const deleteAble = props.isMyMap || props.userInfo?.membership?.gradeCode === 0 || item.userId === userId; // 마이페이지 | 관리자 | 지도작성자Id === 로그인유저Id

        // 25.09.05 | Kim Seong Min |
        const isPrivateMap = props.isMyMap && item.private;

        const menusItem: MenusItem[] = getMenuItem(isLogin, deleteAble);
        return (
            <MapListItem
                key={`mapItem_${index}`}
                item={item}
                menusItem={menusItem}
                onClickItem={onClickItem}
                onClickMenus={onClickMenus}
                isActive={isActive}
                isPrivateMap={isPrivateMap}
                isMyMap={props.isMyMap}
            />
        );
    };

    return (
        <>
            <ModalComponent />
            {props.title && <ContentTitle title={props.title} />}
            {data && data.length ? (
                <>
                    <div className={styles.container}>
                        {data.map((item: MapItem, index: number) => {
                            return renderItem(item, index);
                        })}
                    </div>
                </>
            ) : (
                <NoContent msg={"지도가 없습니다."} icon="travel_explore" />
            )}{" "}
            <Pagination
                currentPageNo={currentPageNo}
                lastPageNo={lastPageNo}
                onPageChanged={onPageChanged}
                searchFilters={getSearchFilters()}
                doSearch={doSearch}
            />
        </>
    );
};

export default MapListView;
